"use client";
import { type Template } from "@zapier/templates";
import { FeaturedSolutionsSection } from "./FeaturedSolutionsSection";
import { Hero } from "./Hero";
import styles from "./TemplatesHomepages.module.css";
import { UseCasesSection } from "./UseCasesSection";
import { GallerySection } from "./GallerySection";
import { ProductsSection } from "./ProductsSection";
import { TemplatesSection } from "./TemplatesSection";
import { TemplatesTabs } from "src/components/TemplatesTabs";
import { components } from "@zapier/api-clients/apis/templates-backend";
import { type TemplateShowcaseItem } from "src/types";
import { useTranslations } from "next-intl";
import { useWebMcpCategoryTool } from "src/hooks/useWebMcpCategoryTool";

interface TemplatesHomepageProps {
  templates: Template[];
  categories: components["schemas"]["Category"][];
  showcaseUseCases?: TemplateShowcaseItem[];
  galleries?: components["schemas"]["Gallery"][];
  products: components["schemas"]["Product"][];
  tabsComponent?: React.ReactNode;
}

export const TemplatesHomepage = ({
  categories,
  templates,
  showcaseUseCases,
  galleries,
  products,
}: TemplatesHomepageProps) => {
  const t = useTranslations("Sections");

  // AGD-28: expose the category filter as a WebMCP tool for browser AI agents.
  // No-op in browsers without WebMCP support.
  useWebMcpCategoryTool(categories);

  const featuredTemplates = templates.filter((template) => !!template.featured);
  const standardTemplates = templates.filter((template) => !template.featured);

  return (
    <>
      <Hero />
      <TemplatesTabs />
      <div className={styles.container}>
        <UseCasesSection categories={categories} />
        {showcaseUseCases && showcaseUseCases.length > 0 && (
          <FeaturedSolutionsSection featuredSolutions={showcaseUseCases} />
        )}
        {galleries && galleries.length > 0 && (
          <GallerySection galleries={galleries} />
        )}
        <ProductsSection products={products} />
        <TemplatesSection
          title={t("featuredTemplates")}
          templates={featuredTemplates}
        />
        <TemplatesSection
          title={t("allTemplates")}
          templates={standardTemplates}
        />
      </div>
    </>
  );
};
